import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductService } from '../shared/product.service';
import { Edit, Info, LucideAngularModule, Plus, Save, Trash } from 'lucide-angular';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProductModel } from '../shared/product.model';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { ProductVariantModel } from '../shared/product-variant.model';
import { ProductVariantOptionsComponent } from './product-variant-options.component';
import { ProductVariantService } from '../shared/product-variant.service';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { NotificationService } from '@app/shared/notification/notification.service';
import { ToastService } from '@app/shared/toast/toast.service';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { catchError, of, Subject, switchMap, takeUntil, takeWhile, timer } from 'rxjs';

type UsageDetails = {
  events: Array<{ id: number; name: string; reference?: string | null; date: string | null }>;
  event_count: number;
};

@Component({
  selector: 'app-edit',
  imports: [
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    TextInputComponent,
    SelectComponent,
    ProductVariantOptionsComponent,
    LucideAngularModule,
    NgxEditorModule,
    ModalComponent,
    CheckboxComponent,
    TippyDirective,
    DatePickerComponent,
  ],
  templateUrl: './product-edit.component.html',
  styleUrl: './product-edit.component.css',
})
export class ProductEditComponent extends AbstractEntityEditComponent implements OnInit, OnDestroy {
  protected processing = false;
  protected validationErrors: string[] = [];
  protected product!: ProductModel;
  protected duplicateModalOpen = false;
  protected duplicateProcessing = false;
  protected duplicateStatus?: 'queued' | 'processing' | 'finished' | 'failed' | 'cancelled';
  private destroy$ = new Subject<void>();

  protected readonly SaveIcon = Save;
  protected readonly PlusIcon = Plus;
  protected readonly EditIcon = Edit;
  protected readonly InfoIcon = Info;
  protected readonly TrashIcon = Trash;

  protected variantNameControl = new FormControl<string | null>('', [Validators.maxLength(255)]);
  protected variantDescriptionControl = new FormControl<string | null>('', [Validators.maxLength(255)]);
  protected variantEditForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    description: new FormControl('', [Validators.maxLength(255)]),
    available_from: new FormControl<string>(''),
    available_until: new FormControl<string>(''),
  });
  protected variantsPage?: PagedEntities<ProductVariantModel>;
  protected editingVariantId: number | null = null;
  protected activeOptionVariantId: number | null = null;
  protected variantEditError?: string;
  protected variantCreateError?: string;

  protected get sortedVariants(): ProductVariantModel[] {
    return [...(this.variantsPage?.data || [])].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
  }

  variantToDelete?: ProductVariantModel;
  variantUsageDetails?: UsageDetails;
  variantUsageContext: 'delete' | 'view' = 'delete';
  variantUsageVariant?: ProductVariantModel;

  protected categoryChoices: { value: ProductModel['category']; label: string }[] = [
    { value: 'Venue Hire', label: 'Venue Hire' },
    { value: 'Accommodation', label: 'Accommodation' },
    { value: 'Wedding Morning Ideas – Food', label: 'Wedding Morning Ideas – Food' },
    { value: 'Wedding Morning Ideas – Drink', label: 'Wedding Morning Ideas – Drink' },
    { value: 'Canapes', label: 'Canapes' },
    { value: 'Reception Drinks', label: 'Reception Drinks' },
    { value: 'Wedding Breakfast Menus', label: 'Wedding Breakfast Menus' },
    { value: 'Drinks Packages', label: 'Drinks Packages' },
    { value: 'Wine List', label: 'Wine List' },
    { value: 'Evening Food', label: 'Evening Food' },
    { value: 'Evening Food – Complimentary', label: 'Evening Food – Complimentary' },
    { value: 'Complimentary Items', label: 'Complimentary Items' },
    { value: 'Extras – Food', label: 'Extras – Food' },
    { value: 'Extras – Drink', label: 'Extras – Drink' },
    { value: 'DJ & Entertainment', label: 'DJ & Entertainment' },
    { value: 'Hire Items', label: 'Hire Items' },
    { value: 'Décor', label: 'Décor' },
    { value: 'Floristry', label: 'Floristry' },
    { value: 'Miscellaneous', label: 'Miscellaneous' },
    { value: 'Supplier Meals', label: 'Supplier Meals' },
    { value: 'Security Retainer', label: 'Security Retainer' },
  ];

  statusChoices = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
  ];


  imageUrl: string | ArrayBuffer | null = null;
  imageFile: File | null = null;

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    description: new FormControl(''),
    price: new FormControl('', [Validators.required, Validators.pattern(/^-?\d+(\.\d{1,2})?$/)]),
    in_stock: new FormControl(true, Validators.required),
    status: new FormControl('', [Validators.required, Validators.pattern(/^(Active|Inactive)$/)]),
    category: new FormControl('', [
      Validators.required,
      Validators.pattern(
        /^(Venue Hire|Accommodation|Wedding Morning Ideas – Food|Wedding Morning Ideas – Drink|Canapes|Reception Drinks|Wedding Breakfast Menus|Drinks Packages|Wine List|Evening Food|Evening Food – Complimentary|Complimentary Items|Extras – Food|Extras – Drink|DJ & Entertainment|Hire Items|Décor|Floristry|Miscellaneous|Supplier Meals|Security Retainer)$/,
      ),
    ]),
    is_minimum_spend: new FormControl(true, Validators.required), // Default to true for new products
    is_variant_based: new FormControl(false),
    counts_to_500_deal: new FormControl(false),
    inc_in_150_free: new FormControl(false),
    triggers_accommodation_tab: new FormControl(false),
    available_from: new FormControl<string>(''),
    available_until: new FormControl<string>(''),
  });

  editor?: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list', 'link'],
    ['text_color', 'background_color'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['undo', 'redo'],
  ];

  constructor(
    private productService: ProductService,
    private variantService: ProductVariantService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private toastService: ToastService,
  ) {
    super();
    this.form.valueChanges.subscribe((value) => {
      if (this.form.dirty && !this.form.valid) {
        this.validationErrors = this.getFormValidationErrors(this.form);
      }
    });

    // VG-395: Listen for is_variant_based changes to toggle price validators
    this.form.controls.is_variant_based.valueChanges.subscribe((isVariantBased) => {
      const priceControl = this.form.controls.price;
      if (isVariantBased) {
        // Make price optional when variant-based
        priceControl.clearValidators();
        priceControl.setValidators([Validators.pattern(/^-?\d+(\.\d{1,2})?$/)]);
      } else {
        // Make price required when not variant-based
        priceControl.setValidators([Validators.required, Validators.pattern(/^-?\d+(\.\d{1,2})?$/)]);
      }
      priceControl.updateValueAndValidity();
    });

    if (this.route.snapshot.data['product']) {
      this.setProduct(this.route.snapshot.data['product']);
    } else {
      const product = {} as ProductModel;
      product.images = [];
      product.status = 'Active';
      product.price_can_be_changed = true;
      product.in_stock = true;
      this.setProduct(product);
    }
  }

  ngOnInit(): void {
    this.editor = new Editor({
      history: true,
      keyboardShortcuts: true,
    });

    // Initial data from resolver + subsequent param changes (runGuardsAndResolvers: 'paramsChange')
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data['product']?.data) {
        this.setProduct(data['product'].data);
        this.form.markAsPristine();
        this.scrollToTop();
      }
    });
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private prepareSaveData(): FormData {
    const formData = new FormData();
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        formData.append(key, control.value);
      }
    });
    if (this.imageFile) {
      formData.append('image', this.imageFile);
    }
    return formData;
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.processing = true;

    this.productService.saveFormData(this.prepareSaveData(), this.product?.id).subscribe({
      next: (res) => {
        const isNewProduct = !this.product?.id;
        this.processing = false;
        this.form.markAsPristine(); // Mark form as pristine before navigation to prevent leave warning
        this.toastService.success(isNewProduct ? 'Product created successfully' : 'Product updated successfully');
        this.router.navigate(['/products']);
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
        this.toastService.error('Failed to save product');
      },
    });
  }

  saveAndContinue() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.processing = true;

    const isNewProduct = !this.product?.id;

    this.productService.saveFormData(this.prepareSaveData(), this.product?.id).subscribe({
      next: (res) => {
        this.processing = false;
        this.setProduct(res.data);
        this.form.markAsPristine();
        this.toastService.success(isNewProduct ? 'Product created successfully' : 'Product updated successfully');

        // Navigate to edit page only if creating a new product
        if (isNewProduct && res.data?.id) {
          this.router.navigate(['/products/' + res.data.id + '/edit']);
        }
        // Otherwise stay on current edit page (no navigation)
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
        this.toastService.error('Failed to save product');
      },
    });
  }

  async cancel() {
    if (this.form.dirty) {
      const confirmLeave = await this.confirmationModal.confirm({
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        confirmLabel: 'Leave Page',
        cancelLabel: 'Stay Here',
        confirmButtonStyle: 'danger',
      });

      if (!confirmLeave) {
        return;
      }
    }

    this.router.navigate(['/products']);
  }

  get canSave(): boolean {
    return this.form.valid && this.form.dirty && !this.processing;
  }

  get isCreateMode(): boolean {
    return !this.product?.id;
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      const reader = new FileReader();
      this.imageFile = file;

      reader.onload = () => {
        this.imageUrl = reader.result;
        this.form.markAsDirty();
      };

      reader.readAsDataURL(file);
    }
  }

  private setProduct(product: any) {
    this.product = product;
    this.variantsPage = { data: product.variants || [] } as PagedEntities<ProductVariantModel>;
    this.imageUrl = null;
    this.imageFile = null;

    this.form.reset({
      name: this.product.name || '',
      description: this.product.description || '',
      price: this.product.price !== undefined && this.product.price !== null ? String(this.product.price) : '',
      in_stock: this.product.in_stock ?? true,
      status: this.product.status || 'Active',
      category: this.product.category || '',
      is_minimum_spend: this.product.is_minimum_spend ?? true,
      is_variant_based: this.product.is_variant_based ?? false,
      counts_to_500_deal: this.product.counts_to_500_deal ?? false,
      inc_in_150_free: this.product.inc_in_150_free ?? false,
      triggers_accommodation_tab: this.product.triggers_accommodation_tab ?? false,
      available_from: this.product.available_from ?? '',
      available_until: this.product.available_until ?? '',
    });

    this.variantNameControl.reset();
    this.variantDescriptionControl.reset();
    this.variantEditForm.reset({ name: '', description: '', available_from: '', available_until: '' });
    this.editingVariantId = null;
    this.activeOptionVariantId = null;

    const priceControl = this.form.get('price');
    if (!this.product.price_can_be_changed) {
      priceControl?.disable({ emitEvent: false });
    } else {
      priceControl?.enable({ emitEvent: false });
    }
  }

  addVariant() {
    this.variantCreateError = undefined;
    const name = this.variantNameControl.value?.trim();
    if (!name || this.variantNameControl.invalid) {
      return;
    }

    this.variantService
      .save({
        name,
        description: this.variantDescriptionControl.value?.trim() || '',
        product_id: this.product.id,
      } as unknown as ProductVariantModel)
      .subscribe({
        next: (res) => {
          // add the new variant to the variants list as first item
          this.variantsPage?.data.unshift(res.data);
          this.variantNameControl.reset();
          this.variantDescriptionControl.reset();
        },
        error: (res) => {
          this.variantCreateError = res.error?.message || 'Failed to add variant';
          this.variantNameControl.setErrors({ server: this.variantCreateError });
        },
      });
  }

  startVariantEdit(variant: ProductVariantModel) {
    if (!variant.id) {
      return;
    }
    this.activeOptionVariantId = null;
    this.editingVariantId = variant.id;
    this.variantEditError = undefined;
    this.variantEditForm.reset({
      name: variant.name || '',
      description: variant.description || '',
      available_from: variant.available_from ?? '',
      available_until: variant.available_until ?? '',
    });
  }

  saveVariantEdit() {
    if (!this.editingVariantId || this.variantEditForm.invalid) {
      this.variantEditForm.markAllAsTouched();
      return;
    }

    const name = this.variantEditForm.get('name')?.value?.trim();
    if (!name) {
      this.variantEditForm.get('name')?.setErrors({ required: true });
      return;
    }

    this.variantService
      .save({
        id: this.editingVariantId,
        product_id: this.product.id,
        name,
        description: this.variantEditForm.get('description')?.value?.trim() || '',
        available_from: this.variantEditForm.get('available_from')?.value || null,
        available_until: this.variantEditForm.get('available_until')?.value || null,
      } as ProductVariantModel)
      .subscribe({
        next: (response) => {
          const variants = this.variantsPage?.data || [];
          const index = variants.findIndex((item) => item.id === this.editingVariantId);
          if (index !== -1) {
            variants[index] = response.data;
          }
          this.editingVariantId = null;
          this.variantEditForm.reset({ name: '', description: '', available_from: '', available_until: '' });
        },
        error: (res) => {
          this.variantEditError = res.error?.message || 'Failed to update variant';
        },
      });
  }

  cancelVariantEdit() {
    this.editingVariantId = null;
    this.variantEditError = undefined;
    this.variantEditForm.reset({ name: '', description: '', available_from: '', available_until: '' });
  }

  openOptionForm(variantId: number) {
    this.activeOptionVariantId = variantId;
    this.editingVariantId = null;
    this.variantEditError = undefined;
  }

  closeOptionForm() {
    this.activeOptionVariantId = null;
  }

  deleteVariant(variant: ProductVariantModel) {
    this.variantToDelete = variant;
  }

  openVariantUsage(variant: ProductVariantModel) {
    if (!variant.id) {
      return;
    }

    this.variantUsageContext = 'view';
    this.variantUsageVariant = variant;

    this.variantService.getUsage(variant.id).subscribe({
      next: (response) => {
        this.variantUsageDetails = response;
      },
      error: () => {
        this.toastService.error('Failed to load variant usage');
        this.closeVariantUsageModal();
      },
    });
  }

  confirmDelete() {
    if (this.variantToDelete && this.variantToDelete.id) {
      this.variantService.delete(this.variantToDelete.id.toString()).subscribe({
        next: () => {
          if (this.variantsPage) {
            this.variantsPage.data = this.variantsPage.data.filter(
              (variant) => variant.id !== this.variantToDelete?.id,
            );
          }
          this.toastService.success('Variant deleted successfully');
          this.variantToDelete = undefined;
          this.variantUsageDetails = undefined;
          this.variantUsageVariant = undefined;
          this.variantUsageContext = 'delete';
        },
        error: (res) => {
          // Check if the variant is in use and display detailed error
          if (res.status === 400 && res.error?.events) {
            this.variantUsageDetails = {
              events: res.error.events,
              event_count: res.error.event_count || res.error.events.length,
            };
            this.variantUsageContext = 'delete';
            this.variantUsageVariant = this.variantToDelete;
          } else {
            this.toastService.error(res.error?.message || 'Failed to delete variant');
            this.variantToDelete = undefined;
            this.variantUsageDetails = undefined;
            this.variantUsageVariant = undefined;
          }
        },
      });
    }
  }

  closeVariantDeleteModal() {
    this.closeVariantUsageModal();
  }

  closeVariantUsageModal() {
    this.variantToDelete = undefined;
    this.variantUsageDetails = undefined;
    this.variantUsageVariant = undefined;
    this.variantUsageContext = 'delete';
  }

  openDuplicateModal(): void {
    if (!this.product?.id) {
      return;
    }
    this.duplicateModalOpen = true;
  }

  cancelDuplicate(): void {
    if (this.duplicateProcessing) {
      return;
    }
    this.duplicateModalOpen = false;
  }

  duplicateProduct(): void {
    if (!this.product?.id) {
      return;
    }

    const productId = this.product.id;
    this.duplicateProcessing = true;
    this.duplicateStatus = 'queued';

    this.productService.duplicate(productId).subscribe({
      next: (res) => {
        if (res?.status === 'queued' && res.batch_id) {
          this.toastService.success('Product duplication queued');
          this.duplicateStatus = 'queued';
          this.pollDuplicateStatus(res.batch_id);
          return;
        }

        const newId = res?.data?.id;
        if (newId) {
          this.toastService.success('Product duplicated');
          this.duplicateProcessing = false;
          this.duplicateModalOpen = false;
          this.duplicateStatus = undefined;
          this.form.markAsPristine();
          window.location.href = `/products/${newId}/edit`;
          return;
        }

        this.toastService.success('Product duplicated');
        this.duplicateProcessing = false;
        this.duplicateModalOpen = false;
        this.duplicateStatus = undefined;
      },
      error: (error) => {
        if (error?.status === 409 && error?.error?.batch_id) {
          this.toastService.info('Duplication already in progress');
          this.duplicateStatus = 'processing';
          this.pollDuplicateStatus(error.error.batch_id);
          return;
        }

        this.toastService.error('Failed to duplicate product');
        this.duplicateProcessing = false;
        this.duplicateModalOpen = false;
        this.duplicateStatus = undefined;
      },
    });
  }

  protected duplicateNamePreview(): string {
    const name = this.product?.name || '';
    if (!name) {
      return 'Copy';
    }
    const preview = `${name} (Copy)`;
    return preview.length > 255 ? preview.slice(0, 255) : preview;
  }

  private pollDuplicateStatus(batchId: string): void {
    timer(0, 2000)
      .pipe(
        switchMap(() => this.productService.getDuplicateStatus(batchId)),
        takeWhile(
          (response) => response.status === 'queued' || response.status === 'processing',
          true,
        ),
        catchError(() => {
          this.toastService.error('Failed to check duplication status');
          this.duplicateProcessing = false;
          this.duplicateModalOpen = false;
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        if (response.status === 'finished') {
          this.toastService.success('Product duplicated');
          this.duplicateProcessing = false;
          this.duplicateModalOpen = false;
          this.duplicateStatus = response.status;
          this.form.markAsPristine();
          if (response.product_id) {
            window.location.href = `/products/${response.product_id}/edit`;
          }
          return;
        }

        if (response.status === 'failed' || response.status === 'cancelled') {
          this.toastService.error('Product duplication failed');
          this.duplicateProcessing = false;
          this.duplicateModalOpen = false;
          this.duplicateStatus = response.status;
        }

        this.duplicateStatus = response.status as typeof this.duplicateStatus;
      });
  }

  protected duplicateStatusLabel(): string {
    switch (this.duplicateStatus) {
      case 'queued':
        return 'Queued';
      case 'processing':
        return 'Processing';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      case 'finished':
        return 'Finished';
      default:
        return 'Starting';
    }
  }

  private scrollToTop(): void {
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  }

  protected getForms(): FormGroup<any>[] {
    return [this.form];
  }

}
